import { ApiResponseError } from "../../../shared/services/ApiResponseError";
import type {
  HydrateResponse,
  KioskStatus,
  LookupResult,
  MeetingData,
  SubmitRequestBody,
  SubmitResponse
} from "../types";

/** Unwraps CityzenApiCore's envelope. Failures surface as ApiResponseError, never as the body. */
async function unwrapApiResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new ApiResponseError(res.status, await readErrorCode(res));
  }
  const envelope = await res.json();
  if (envelope && typeof envelope === "object" && "data" in envelope) {
    if (envelope.success === false) {
      throw new ApiResponseError(res.status, readEnvelopeErrorCode(envelope));
    }
    return envelope.data as T;
  }
  return envelope as T;
}

async function readErrorCode(res: Response): Promise<string | null> {
  try {
    return readEnvelopeErrorCode(await res.json());
  } catch {
    return null;
  }
}

function readEnvelopeErrorCode(envelope: unknown): string | null {
  if (!envelope || typeof envelope !== "object") return null;
  const errors = (envelope as { errors?: unknown }).errors;
  if (!errors || typeof errors !== "object") return null;
  const code = (errors as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
}

/** Fetch agenda + meeting metadata from the kiosk's data endpoint. */
export async function fetchMeetingData(dataUrl: string): Promise<MeetingData> {
  const res = await fetch(dataUrl, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit"
  });
  return unwrapApiResponse<MeetingData>(res);
}

/** Poll the lightweight status endpoint for live agenda-item speaker-registration
 *  state. Returns only { agendaItems: [{ id, statusId, acceptingSpeakers }] } so
 *  it's cheap to call on a 30s interval (AB#8889). Callers treat failures as
 *  non-fatal — the submit-side droppedItems guard is the hard backstop. */
export async function fetchMeetingStatus(
  statusUrl: string
): Promise<KioskStatus> {
  const res = await fetch(statusUrl, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit"
  });
  return unwrapApiResponse<KioskStatus>(res);
}

export async function lookupReturningUser(
  lookupUrl: string,
  args: { name?: string; email?: string; phone?: string }
): Promise<LookupResult[]> {
  const params = new URLSearchParams();
  if (args.name) params.set("name", args.name);
  if (args.email) params.set("email", args.email);
  if (args.phone) params.set("phone", args.phone);
  const sep = lookupUrl.includes("?") ? "&" : "?";
  const url = `${lookupUrl}${sep}${params.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit"
  });
  return unwrapApiResponse<LookupResult[]>(res);
}

export async function hydrateRsvp(
  hydrateUrl: string,
  lookupToken: string
): Promise<HydrateResponse> {
  const url = `${hydrateUrl}?token=${encodeURIComponent(lookupToken)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "omit"
  });
  return unwrapApiResponse<HydrateResponse>(res);
}

/** POST the final check-in. Returns the persisted rsvpId, the resolved
 *  offlineEventId, and any items the server dropped because they closed
 *  for sign-up mid-flow (race condition). */
export async function submitCheckIn(
  submitUrl: string,
  body: SubmitRequestBody
): Promise<SubmitResponse> {
  const res = await fetch(submitUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    credentials: "omit",
    body: JSON.stringify(body)
  });
  return unwrapApiResponse<SubmitResponse>(res);
}
